import Card from "@/components/Card";
import SectionHeader from "@/components/SectionHeader";
import SkeletonRow from "@/components/SkeletonRow";
import Stepsbar from "@/components/Stepsbar";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { Button } from "@/components/ui/button";
import useDataPendaftaran from "@/hooks/useDataPendaftaran";
import { useMultistepForm } from "@/hooks/useMultistepForm";
import useScreen from "@/hooks/useScreen";
import useTimePeriode from "@/hooks/useTimePeriode";
import { useUser } from "@/hooks/useUser";
import api from "@/lib/axios";
import { getDataStep, getListBerkasPenerimaan } from "@/services/getData";
import { BiodataSate, DataSiswa, Step, SysBerkas } from "@/types";
import { AxiosError } from "axios";
import { Loader2 } from "lucide-react";
import { useEffect, useState } from "react";
import toast from "react-hot-toast";
import { LuChevronRight } from "react-icons/lu";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Navigate, useNavigate, useSearchParams } from "react-router-dom";
import Biodata from "./partial/biodata/Biodata";
import KartuPeserta from "./partial/kartu-peserta/KartuPeserta";
import Prodi from "./partial/prodi/Prodi";
import UnggahBerkas from "./partial/unggah-berkas/UnggahBerkas";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { IoWarning } from "react-icons/io5";
import useSessionStorage from "@/hooks/useSessionStorage";
import { formatDateIndonesia } from "@/lib/utils";
import { flushSync } from "react-dom";

const saveBiodata = async (form: FormData) => {
  const response = await api.post(
    "/siswa/penerimaan/smbjm/biodata/update",
    form,
    { withCredentials: true },
  );
  return response.data;
};

export default function PenerimaanRplPage() {
  const { user } = useUser();
  const { isMobile } = useScreen();
  const [searchParams] = useSearchParams();
  // const sessionPeriode?.id_sys_periode = searchParams.get("periode");
  // const stepParams = searchParams.get("step");
  const [sessionPeriode, setSessionPeriode] = useSessionStorage(
    "sessionPeriode",
    {
      id_sys_periode: "",
    },
  );

  const { dataPeriode } = useTimePeriode();
  const { isValidPendaftaran } = useDataPendaftaran();
  const { isPendingPeriode, timePassed, timePassedActive, dataPeriodeActive } =
    useTimePeriode();
  const navigate = useNavigate();

  const { data: dataStep } = useQuery({
    queryKey: ["dataStep", user?.id, sessionPeriode?.id_sys_periode],
    queryFn: () => getDataStep(sessionPeriode?.id_sys_periode || ""),
  });
  const idDataPendaftaran = dataStep?.id_data_pendaftaran;

  const { data: dataBerkas } = useQuery({
    queryKey: ["listDataBerkas", sessionPeriode?.id_sys_periode],
    queryFn: () =>
      getListBerkasPenerimaan(sessionPeriode?.id_sys_periode || ""),
  });

  const [isPendingSteps, setisPendingSteps] = useState(false);
  const [steps, setSteps] = useState<Step[]>([
    {
      number: 1,
      text: "Biodata",
      link: "#",
      status: "active",
      tabStatus: "active",
    },
    { number: null, text: null, link: null, status: "nonactive" },
    {
      number: 2,
      text: "Prodi",
      link: "#",
      status: "nonactive",
      tabStatus: "nonactive",
    },
    {
      number: null,
      text: null,
      link: null,
      status: "nonactive",
    },
    {
      number: 3,
      text: "Berkas",
      link: "#",
      status: "nonactive",
      tabStatus: "nonactive",
    },
    {
      number: null,
      text: null,
      link: null,
      status: "nonactive",
    },
    {
      number: 4,
      text: "Pembayaran & Kartu Peserta",
      link: "#",
      status: "nonactive",
      tabStatus: "nonactive",
    },
  ]);
  const [currentIndexDisable, setCurrentIndexDisable] = useState(1);
  const [listPanduan, setListPanduan] = useState([
    {
      title: "Biodata",
      contents: [
        {
          title: "Berkas yang dibutuhkan",
          instructions: ["Foto resmi berukuran 4 x 6"],
        },
        {
          title: "Tata cara pengisian",
          instructions: [
            "Klik kolom yang mau diisi",
            "Isi data dengan benar sesuai kolom yang diminta",
          ],
        },
      ],
    },
    {
      title: "Prodi",
      contents: [
        {
          title: "Tata cara pengisian",
          instructions: [
            "Lihat daftar program studi yang tersedia",
            "Pilih program studi",
            "Pilik konsentrasi program studi (jika ada)",
            "Pilik lokasi kuliah (jika ada)",
            "Tekan 'Simpan'",
          ],
        },
      ],
    },
    {
      title: "Berkas",
      contents: [
        {
          title: "Berkas yang dibutuhkan",
          instructions: [
            "Dapat dilihat pada tabel. (Berkas yang berisi label (*Wajib) adalah berkas yang wajib untuk dipload.",
          ],
        },
        {
          title: "Tata cara pengisian",
          instructions: [
            "Khusus untuk berkas yang memiliki format (ditandai dengan Tombol Unduh pada kolom Format) lakukan unduh terlebih dahulu",
            "Tekan tombol 'Upload'",
            "Pilih file berkas",
            "Isikan nomor berkas jika ada",
            "Tekan tombol 'Simpan File Berkas'",
          ],
        },
      ],
    },
    {
      title: "Kartu Peserta",
      contents: [
        {
          title: "Instruksi",
          instructions: [
            "Pastikan seluruh isian pada data pendaftaran sudah diisi dengan benar",
            "Tekan 'Validasi Pendaftaran' untuk melakukan validasi pendaftaran",
            "Unduh kartu peserta dengan menekan tombol 'Download'",
          ],
        },
      ],
    },
  ]);

  const [formBiodata, setFormBiodata] = useState<BiodataSate>({
    id_sys_periode: sessionPeriode?.id_sys_periode || "",
    id_data_siswa: 0,
    id_ref_agama: "",
    id_ref_negara: "",
    id_ref_alat_transportasi: "",
    id_ref_jenis_tinggal: "",
    nisn: "",
    nama_lengkap: "",
    handphone: "",
    npwp: "",
    tanggal_lahir: "",
    tempat_lahir: "",
    jenis_kelamin: "L",
    berat_badan: 0,
    tinggi_badan: 0,
    ukuran_baju: "",
    jalan: "",
    rt: "",
    rw: "",
    kode_pos: "",
    kelurahan: "",
    provinsi: "",
    kabupaten: "",
    kecamatan: "",
    nama_panti_asuhan: "",
    kebutuhan_khusus: [] as string[],
    pilihan_lokasi_kuliah: "",
    asal_pt: "",
    asal_prodi: "",
    is_bekerja: undefined,
    tempat_bekerja: "",
    penghasilan_bekerja: "",
    nilai_IPK: "",
    akreditasi_pt_asal: "",
    akreditasi_prodi_asal: "",
    tahun_ijazah_terakhir: "",
    id_ref_sekolah: "",
  });

  const [file, setFile] = useState<File | null>(null);

  const queryClient = useQueryClient();
  const {
    mutate,
    isPending: isPendingSaveBiodata,
    error,
  } = useMutation({
    mutationFn: saveBiodata,
    onSuccess: (data) => {
      queryClient.invalidateQueries({
        queryKey: ["dataStep", user?.id, sessionPeriode?.id_sys_periode],
      });
      toast.success(data?.message || "Success.");
    },
    onError: (
      error: AxiosError<{
        message?: string;
        errors?: Record<string, string[]>;
      }>,
    ) => {
      const err = error as AxiosError<{
        message?: string;
        errors?: Record<string, string[]>;
      }>;
      const errorMessage =
        err.response?.data?.message || "An unexpected error occurred.";
      toast.error(errorMessage);
    },
  });

  const {
    currentStep,
    firstStep,
    lastStep,
    backStep,
    goToStep,
    currentIndex,
    currentStepNumber,
  } = useMultistepForm([
    <Biodata
      form={formBiodata}
      setForm={setFormBiodata}
      file={file}
      setFile={setFile}
      error={error}
    />,
    <Prodi idDataPendaftaran={idDataPendaftaran} />,
    <UnggahBerkas idDataPendaftaran={idDataPendaftaran} />,
    <KartuPeserta />,
  ]);

  const [showPanduan, setShowPanduan] = useState(false);
  // const [visibleCount, setVisibleCount] = useState(5);
  // const handleLoadMore = () => setVisibleCount((prevCount) => prevCount + 5);
  // const panduan = listPanduanPenerimaan[currentIndex];

  const handleSaveBiodata = async () => {
    const formData = new FormData();

    Object.keys(formBiodata).forEach((key) => {
      const value = formBiodata[key as keyof DataSiswa];
      if (Array.isArray(value)) {
        // Handle array (kebutuhan_khusus)
        value.forEach((item, index) => {
          if (item !== null && item !== undefined) {
            formData.append(`${key}[${index}]`, String(item)); // Konversi ke string menggunakan String()
          }
        });
      } else {
        // Handle nilai non-array
        if (value !== null && value !== undefined) {
          formData.append(key, String(value)); // Konversi ke string menggunakan String()
        }
      }
    });
    if (file) formData.append("foto", file);

    mutate(formData);
  };

  const handleNextStep = () => {
    switch (currentStepNumber) {
      case 1:
        if (dataStep?.data_diri === 1) {
          goToStep(2);
          searchParams.set("step", "2");
          navigate(`${location.pathname}?${searchParams.toString()}`);
        }
        break;
      case 2:
        if (dataStep?.data_pilihan_prodi === 1) {
          goToStep(3);
          searchParams.set("step", "3");
          navigate(`${location.pathname}?${searchParams.toString()}`);
        }
        break;
      case 3:
        if (dataStep?.data_berkas === 1) {
          goToStep(4);
          searchParams.set("step", "4");
          navigate(`${location.pathname}?${searchParams.toString()}`);
        }
        break;
    }
  };

  useEffect(() => {
    setisPendingSteps(true);
    if (!dataStep) return;

    setSteps((prev) => {
      const updatedSteps = [...prev];

      // Helper function untuk update status connector (null steps)
      const updateConnectorStatus = (index: number) => {
        if (index <= 0 || index >= updatedSteps.length) return;

        const prevStep = updatedSteps[index - 1];
        if (updatedSteps[index].number === null) {
          updatedSteps[index].status =
            prevStep.status === "completed" ? "completed" : "nonactive";
        }
      };

      // Update main steps status
      updatedSteps.forEach((step, index) => {
        if (!step.text) return; // Skip connector steps

        switch (step.text) {
          case "Biodata":
            if (dataStep?.data_diri === 1) {
              step.status = "completed";
              step.tabStatus = "active";
              if (updatedSteps[index + 2]) {
                updatedSteps[index + 2].status = "active";
                updatedSteps[index + 2].tabStatus = "active";
              }
              goToStep(2);
              setCurrentIndexDisable(2);
            }
            break;

          case "Prodi":
            if (dataStep?.data_pilihan_prodi === 1) {
              step.status = "completed";
              step.tabStatus = "active";
              if (updatedSteps[index + 2]) {
                updatedSteps[index + 2].status = "active";
                updatedSteps[index + 2].tabStatus = "active";
              }
              goToStep(3);
              setCurrentIndexDisable(3);
            }
            break;
          case "Berkas":
            if (dataStep?.data_berkas === 1) {
              step.status = "completed";
              step.tabStatus = "active";
              if (updatedSteps[index + 2]) {
                updatedSteps[index + 2].status = "active";
                updatedSteps[index + 2].tabStatus = "active";
              }
              // goToStep(4);
              setCurrentIndexDisable(4);
            }
            break;
          case "Pembayaran & Kartu Peserta":
            if (dataStep?.valid_is_pendaftaran === 1) {
              step.status = "completed";
              step.tabStatus = "active";
              if (updatedSteps[index + 2]) {
                updatedSteps[index + 2].status = "active";
                updatedSteps[index + 2].tabStatus = "active";
              }
              goToStep(4);
            }
            break;
        }
      });

      // Update connector steps
      updatedSteps.forEach((_, index) => {
        updateConnectorStatus(index);
      });

      return updatedSteps;
    });

    setisPendingSteps(false);
  }, [goToStep, dataStep]);

  useEffect(() => {
    setSteps((prev) =>
      prev.map((item) => {
        if (item.number != currentStepNumber)
          return { ...item, tabStatus: "nonactive" };
        return { ...item, tabStatus: "active" };
      }),
    );
  }, [currentStepNumber]);

  useEffect(() => {
    if (isMobile) setShowPanduan(false);
    else setShowPanduan(true);
  }, [isMobile]);

  useEffect(() => {
    if (!dataBerkas) return;

    const listNamaBerkas = dataBerkas?.listSysBerkas?.map((row: SysBerkas) => {
      return row.jenis_berkas?.nama_jenis_berkas;
    });

    setListPanduan((prev) =>
      prev?.map((value) => {
        if (value.title.startsWith("Berkas")) {
          return {
            ...value,
            contents: value.contents.map((content) => {
              if (content.title.startsWith("Berkas")) {
                return {
                  ...content,
                  instructions: listNamaBerkas,
                };
              }
              return content;
            }),
          };
        }
        return value;
      }),
    );
  }, [dataBerkas]);

  useEffect(() => {
    if (timePassed.waktu_pengumuman_kelulusan) {
      flushSync(() => {
        setSessionPeriode({
          id_sys_periode: dataPeriode?.id_sys_periode || "",
        });
      });
      navigate(`/pengumuman-kelulusan`);
    }
    if (timePassed.waktu_pengumuman) {
      flushSync(() => {
        setSessionPeriode({
          id_sys_periode: dataPeriode?.id_sys_periode || "",
        });
      });
      navigate(`/pengumuman`);
    }
  }, [timePassed, navigate, sessionPeriode?.id_sys_periode]);

  if (isPendingPeriode) {
    return (
      <div className="flex flex-col gap-y-3">
        {Array(10)
          .fill(null)
          .map((_, index) => {
            return <SkeletonRow key={index} />;
          })}
      </div>
    );
  }

  if (
    dataPeriode &&
    dataPeriode?.jalur_jenis?.nama_jalur_jenis_slug !== "rpl"
  ) {
    return <Navigate to="/dashboard" />;
  }

  return (
    <main>
      <Alert variant={"warning"} className="z-30 mb-5 sticky top-0">
        <IoWarning />
        <AlertTitle>
          <strong>Perhatian:</strong>
        </AlertTitle>
        <AlertDescription>
          <div className="mb-3">
            <div className="font-semibold">
              {timePassedActive?.waktu_selesai_daftar_valid_selesai ? (
                <h1>
                  Waktu proses pendaftaran telah berakhir pada
                  <span className="text-primary">
                    {" "}
                    {dataPeriodeActive?.waktu_selesai_daftar_valid &&
                      formatDateIndonesia(
                        dataPeriodeActive?.waktu_selesai_daftar_valid || "",
                      )}
                  </span>
                </h1>
              ) : (
                ""
              )}
            </div>
            <p>
              Pendaftaran dianggap selesai jika sudah{" "}
              <span className="text-primary font-semibold">
                membayar biaya pendaftaran
              </span>{" "}
              dan mendapatkan Kartu Peserta
            </p>
          </div>
          <div>
            <h1 className="font-semibold">Waktu pengumuman</h1>
            <p>
              Waktu pengumuman dimulai pada{" "}
              <span className="text-primary font-semibold">
                {dataPeriode?.waktu_pengumuman &&
                  formatDateIndonesia(dataPeriode?.waktu_pengumuman)}
              </span>
            </p>
          </div>
        </AlertDescription>
      </Alert>
      <SectionHeader
        title={`Penerimaan - ${dataPeriode?.jalur_jenis?.nama_jalur_jenis}`}
      />
      <div className="flex lg:flex-col flex-row lg:gap-10">
        <Stepsbar
          steps={steps}
          goToStep={goToStep}
          isPending={isPendingSteps}
        />
        <div className="flex-1 h-screen overflow-auto">
          <div className="flex lg:flex-row flex-col lg:gap-5 gap-3">
            {currentStepNumber !== 2 && (
              <Card className={`${showPanduan ? "lg:w-[20rem]" : ""} h-fit`}>
                <div className="flex justify-between">
                  {showPanduan && (
                    <h1 className="mb-5 font-bold text-xl">Panduan</h1>
                  )}
                  <div className="flex items-center flex-col gap-y-3">
                    {!showPanduan && (
                      <span className="text-sm font-bold">Panduan</span>
                    )}
                    <Button
                      variant={"outline"}
                      size={"icon"}
                      onClick={() => setShowPanduan((show) => !show)}
                    >
                      <LuChevronRight
                        className={`${
                          showPanduan ? "rotate-90" : "rotate-0"
                        } duration-300`}
                      />
                    </Button>
                  </div>
                </div>
                <section className="flex flex-col gap-y-3">
                  {showPanduan &&
                    listPanduan.map((panduan, index) => {
                      return (
                        <div key={index}>
                          <Accordion type="single" collapsible>
                            <AccordionItem value="item-1">
                              <AccordionTrigger className="text-start">
                                {panduan.title}
                              </AccordionTrigger>
                              <AccordionContent>
                                <div className="flex flex-col gap-1">
                                  {panduan.contents.map((content) => {
                                    return (
                                      <div key={content.title}>
                                        <div className="flex items-center gap-1">
                                          <span>-</span>
                                          <h1 className="font-semibold">
                                            {content.title}
                                          </h1>
                                        </div>
                                        <ul>
                                          {content.instructions.map(
                                            (instruction, indexInstruction) => {
                                              return (
                                                <li
                                                  key={indexInstruction}
                                                  className="text-muted-foreground"
                                                >
                                                  <span>
                                                    {indexInstruction + 1}.{" "}
                                                  </span>
                                                  {instruction}
                                                </li>
                                              );
                                            },
                                          )}
                                        </ul>
                                      </div>
                                    );
                                  })}
                                </div>
                              </AccordionContent>
                            </AccordionItem>
                          </Accordion>
                        </div>
                      );
                    })}
                </section>
              </Card>
            )}

            <div className="flex-1 overflow-auto">
              {currentStep}

              <div className="mt-5 flex justify-between">
                {!firstStep && (
                  <Button type="button" variant={"outline"} onClick={backStep}>
                    Kembali
                  </Button>
                )}

                <div className="flex gap-3 w-fit ml-auto">
                  {firstStep &&
                  !isValidPendaftaran &&
                  !timePassedActive?.waktu_selesai_daftar_valid_selesai ? (
                    <Button
                      type="button"
                      variant={"outline"}
                      className={`ml-auto`}
                      disabled={isPendingSaveBiodata}
                      onClick={handleSaveBiodata}
                    >
                      {isPendingSaveBiodata ? (
                        <>
                          <Loader2 className="w-4 h-4 mr-1 animate-spin" />
                          Processing...
                        </>
                      ) : (
                        "Simpan"
                      )}
                    </Button>
                  ) : (
                    ""
                  )}

                  {!lastStep && (
                    <Button
                      type="button"
                      className={`ml-auto ${
                        currentIndex === currentIndexDisable - 1
                          ? "opacity-35 pointer-events-none"
                          : ""
                      }`}
                      disabled={false}
                      onClick={handleNextStep}
                    >
                      Berikutnya
                    </Button>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </main>
  );
}
